import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { LANGUAGE_ENDONYMS, supportedLngs } from "@jackedlogs/i18n";
import type { Locale } from "@jackedlogs/shared";

import { Chip } from "../primitives/Chip";
import { ChoiceTray } from "../primitives/ChoiceTray";
import { useDisclosure } from "../primitives/useDisclosure";
import type { UiTheme } from "../theme";

// PE-21 slice B — the language the program editor is WRITING.
//
// Same shape as the gym chip (`primitives/GymChip.tsx`): a small chip that
// states the current answer, and opens a <ChoiceTray> in place. Chosen
// over SettingsPillChoice deliberately — that one is an always-visible
// Settings card, and Antenore's rule here is that whoever doesn't
// translate must not find this in their way.
//
// ⭐ TWO QUESTIONS, ONE CONTROL. A program written before PE-21 never
// recorded the language of its text, and we refuse to guess it (guessing
// would file Italian prose under `fr` the moment a French reader touched
// it). So when `value` is null the tray asks which language the program
// IS written in, and the answer DECLARES it. Afterwards the same tray
// asks which language you are writing, and the answer SWITCHES. The
// caller gets the two apart, because one of them rewrites the draft and
// the other only labels it.
export type LanguageChipProps = {
  theme: UiTheme;
  /** The language being written, or null when the program never declared one. */
  value: Locale | null;
  /** Sets the language of the text ALREADY there. Nothing is translated. */
  onDeclare: (locale: Locale) => void;
  /** Moves the editor to another language, carrying the draft across. */
  onSwitch: (locale: Locale) => void;
};

export function useLanguageChip({
  theme,
  value,
  onDeclare,
  onSwitch,
}: LanguageChipProps): { trigger: ReactNode; tray: ReactNode } {
  const { t } = useTranslation();
  const disclosure = useDisclosure(false);

  const options = supportedLngs.map((lng) => ({
    key: lng,
    label: LANGUAGE_ENDONYMS[lng],
    selected: lng === value,
  }));

  const trigger = (
    <Chip
      theme={theme}
      selected={false}
      // Stands alone in the meta band with no neighbour to imply a
      // control, so it needs the findable outline (same reason as the gym
      // chip; the default border is the DS-37 defect).
      prominent
      style={styles.trigger}
      label={`${
        value
          ? LANGUAGE_ENDONYMS[value]
          : t("program.languageUndeclared", { defaultValue: "Language" })
      } ${disclosure.expanded ? "▴" : "▾"}`}
      expanded={disclosure.expanded}
      // ⛔ The value goes in the announcement. Without it a screen-reader
      // user hears the same words in every state and never learns WHICH
      // language is active — strictly less than the chip's own visible
      // label gives a sighted user. Same precedent as the gym chip.
      accessibilityLabel={`${t("program.languageA11y", {
        defaultValue: "Writing language",
      })}, ${
        value
          ? LANGUAGE_ENDONYMS[value]
          : t("program.languageUndeclared", { defaultValue: "Language" })
      }`}
      onPress={disclosure.toggle}
    />
  );

  const tray = disclosure.expanded ? (
    <ChoiceTray
      theme={theme}
      label={
        value
          ? t("program.languageSwitchQuestion", {
              defaultValue: "Which language are you writing in?",
            })
          : t("program.languageDeclareQuestion", {
              defaultValue: "Which language is this program written in?",
            })
      }
      options={options}
      onChoose={(key) => {
        disclosure.setExpanded(false);
        if (key === value) return;
        // ⛔ The two are not the same operation. Declaring only labels the
        // text that is already there; switching carries the draft into
        // another language and opens it EMPTY when nothing is stored.
        if (value === null) onDeclare(key as Locale);
        else onSwitch(key as Locale);
      }}
      footnote={
        value
          ? t("program.languageFootnote", {
              defaultValue:
                "Translations are optional. A program in one language is complete.",
            })
          : t("program.languageDeclareFootnote", {
              defaultValue: "Nothing gets translated: this only records the language.",
            })
      }
    />
  ) : null;

  return { trigger, tray };
}

// Same ceiling as the gym chip: an endonym like "Русский" must not push
// the meta band's label onto a second line.
const styles = { trigger: { maxWidth: 160 } } as const;
